// ─── Capture ──────────────────────────────────────────────────────────────
//
// A quick-capture tile: a photo, a video, an audio note, free text or a web
// address you want to keep, optionally filed under a typed section ("Garden", "Jan
// Smith", "Editorial") — left in the Inbox if untagged. 2026-09-14, Drea's
// ask.
//
// ── Scope, said out loud ─────────────────────────────────────────────────
//
// This is a tile in the existing signed-in dashboard, not a separate
// installable phone app with its own offline queue — Mira's own Mobile
// Capture App is that shape, and its frontend is already extracted and
// staged for later if a true installable version is wanted. What this needs
// TODAY (a phone browser, already signed in, quick capture with sections)
// doesn't need a service worker or an IndexedDB queue to deliver — see
// HANDOFF.md in the staged package for the fuller shape if that's revisited.
//
// Deliberately NOT built here, pinned for later (Drea's own "we'll come
// back to that"): editing a written capture's text in place beyond its
// title, trimming audio, or applying a filter to a photo before it's
// stored or shared. Today a capture is stored as taken/dictated/typed.

import { useEffect, useRef, useState } from "react";
// ── Tokens ──────────────────────────────────────────────────────────────────
// Written for Crafting Comb's booking theme (`--nl-surface`, `--nl-state-open`,
// ...). Each of those now falls back to the chassis's equivalent, so the SAME
// screen reads correctly in an app on the chassis theme (NuLife PA) without a
// second copy of it: where the booking tokens exist they win, exactly as before.
import { createContext, useContext } from "react";
import { Loading, Message } from "@nulife/app-ui";
import type { CaptureClient, CaptureItem, CaptureSection, DraftStore } from "./client.js";

const CaptureClientContext = createContext<CaptureClient | null>(null);

/** The app wraps the screen in this with its own client (see client.ts). */
export const CaptureClientProvider = CaptureClientContext.Provider;

function useClient(): CaptureClient {
  const c = useContext(CaptureClientContext);
  if (!c) throw new Error("Capture needs a CaptureClientProvider above it.");
  return c;
}

type LibraryTarget =
  | { kind: "sections" }
  | { kind: "inbox" }
  | { kind: "section"; tag: string; tagKey: string }
  /** Recently deleted, and still restorable. */
  | { kind: "deleted" };

/**
 * New captures and the library, and nothing else.
 *
 * ── What it stopped owning on 2026-09-15 ──────────────────────────────────
 *
 * A title and a "Back to the diary" button. Both made sense while this was a
 * panel swapped into the middle of the booking dashboard; on its own surface
 * they were a second heading under "Captured items" and a way back to an app
 * this one is no longer part of.
 *
 * `CaptureApp` owns the page's title and the links to its sibling surfaces.
 * This owns the capturing. Lifting a panel out of a dashboard and leaving its
 * panel chrome attached is how something ends up looking like it is still
 * inside the thing it was taken out of.
 */
export function Capture({ onNotice, variant = "form", draftStore, moreHint }: {
  onNotice: (message: string) => void;
  /** "tiles" only: where to keep the capture being decided on. */
  draftStore?: DraftStore;
  /** "tiles" only: something shown at the top of the collapsed More menu. */
  moreHint?: React.ReactNode;
  /** "form" is the original screen (Crafting Comb). "tiles" is Drea's
   *  2026-10-27 design for PA: three big tiles, and everything else only after
   *  the capture is taken. */
  variant?: "form" | "tiles";
}) {
  const [mode, setMode] = useState<"capture" | "library">("capture");
  if (variant === "tiles") return <CaptureTiles onNotice={onNotice} draftStore={draftStore} moreHint={moreHint} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button type="button" style={mode === "capture" ? primary : secondary} onClick={() => setMode("capture")}>
          New
        </button>
        <button type="button" style={mode === "library" ? primary : secondary} onClick={() => setMode("library")}>
          Library
        </button>
      </div>
      {mode === "capture"
        ? <CaptureForm onSent={() => setMode("library")} onNotice={onNotice} />
        : <Library onNotice={onNotice} />}
    </div>
  );
}

// ── New capture ──────────────────────────────────────────────────────────

function CaptureForm({ onSent, onNotice }: { onSent: () => void; onNotice: (m: string) => void }) {
  const api = useClient();
  const [note, setNote] = useState("");
  const [link, setLink] = useState("");
  const [tag, setTag] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // One id per attempt at THIS capture, reused by every retry of it. If the
  // first try reached the server but the answer was lost on the way back
  // (poor signal), a retry with a fresh id would store the capture twice; with
  // the same id the server says "already done". It is dropped the moment the
  // content changes, so an edited capture is never mistaken for the old one.
  const attemptId = useRef<string | null>(null);
  useEffect(() => { attemptId.current = null; }, [note, link, tag, files]);

  const hasContent = files.length > 0 || note.trim().length > 0 || link.trim().length > 0;
  // Leaving this screen mid-recording must not leave the microphone open.
  useEffect(() => () => { try { if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop(); } catch { /* already stopped */ } }, []);

  function handlePhotoChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setFiles(prev => [...prev, file]);
    e.target.value = "";
  }

  async function toggleRecording() {
    if (recording) {
      mediaRecorderRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        setFiles(prev => [...prev, new File([blob], `audio-note-${Date.now()}.webm`, { type: blob.type })]);
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch {
      onNotice("Microphone access is needed to record an audio note.");
    }
  }

  async function send() {
    if (!hasContent || sending) return;
    setSending(true);
    try {
      const saved = await api.uploadCapture({
        note: note.trim() || undefined,
        link: link.trim() || undefined,
        tag: tag.trim() || undefined,
        files,
        clientActionId: (attemptId.current ??= crypto.randomUUID()),
      });
      attemptId.current = null;
      setNote(""); setLink(""); setTag(""); setFiles([]);
      onNotice(saved.queued ? waitingNote(saved, "") : "Captured.");
      onSent();
    } catch (err) {
      // Nothing has been cleared: the photos and note are still on this screen,
      // so say that, and that Send is the way to try again.
      const why = err instanceof Error ? err.message : "Could not save the capture.";
      onNotice(`${why} Nothing has been lost — your capture is still here. Tap Send to try again.`);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input ref={photoInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handlePhotoChosen} data-testid="capture-photo-input" />
        <button type="button" style={secondary} onClick={() => photoInputRef.current?.click()}>
          <Icon name="camera" size={18} /> {files.some(f => f.type.startsWith("image/")) ? "Add another photo" : "Take a photo"}
        </button>
        <input ref={videoInputRef} type="file" accept="video/*" capture="environment" style={{ display: "none" }} onChange={handlePhotoChosen} data-testid="capture-video-input" />
        <button type="button" style={secondary} onClick={() => videoInputRef.current?.click()} data-testid="capture-video">
          <Icon name="video" size={18} /> {files.some(f => f.type.startsWith("video/")) ? "Add another video" : "Record a video"}
        </button>
        <button type="button" style={recording ? danger : secondary} onClick={toggleRecording}>
          {recording ? <><Icon name="stop" size={18} /> Stop recording</> : <><Icon name="mic" size={18} /> Record audio note</>}
        </button>
      </div>

      {files.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem" }}>
              <Icon name={f.type.startsWith("image/") ? "camera" : f.type.startsWith("video/") ? "video" : "mic"} size={18} />
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <button type="button" style={{ ...secondary, minHeight: "1.8rem", padding: "0 8px", fontSize: "0.78rem" }}
                onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
        </div>
      )}

      <textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="A note — tap the mic on your keyboard to dictate"
        rows={3}
        style={{ ...field, minHeight: "4.5rem", padding: "8px 12px", resize: "vertical" }}
      />

      <div>
        <label style={hint}>A website you like (optional — paste the address)</label>
        <input
          value={link}
          onChange={e => setLink(e.target.value)}
          placeholder="e.g. www.example.com/page"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          style={field}
          data-testid="capture-link"
        />
      </div>

      <div>
        <label style={hint}>Section (optional — leave blank for the Inbox)</label>
        <input
          value={tag}
          onChange={e => setTag(e.target.value)}
          placeholder="e.g. Garden, Jan Smith, Editorial"
          style={field}
        />
      </div>

      <button
        type="button"
        style={{ ...primary, opacity: hasContent && !sending ? 1 : 0.5, alignSelf: "flex-start" }}
        disabled={!hasContent || sending}
        onClick={send}
      >{sending ? "Sending…" : "Send"}</button>
    </div>
  );
}

// ── Tiles: take first, decide after ─────────────────────────────────────────
//
// Drea (2026-10-27): opening Capture shows three big tiles — photo, video,
// audio — and nothing else, so nobody is distracted by options before they have
// taken anything. Once something is taken a pop-up asks what to do with it:
// Save (straight to the Inbox) or Save to (the folder list — the sections).
// Notes and tags live in that step, not before. The older all-in-one form, the
// library and the "a website you like" capture sit in one collapsed menu.


// ── Flat line icons (Drea, 2026-10-28: no emoji pictures) ────────────────────
type IconName = "camera" | "video" | "mic" | "stop" | "save" | "plus" | "note" | "bin" | "share" | "link";
const ICON_PATHS: Record<IconName, React.ReactNode> = {
  camera: <><path d="M4 8h3l1.5-2h7L17 8h3v11H4z" /><circle cx="12" cy="13.5" r="3.5" /></>,
  video: <><rect x="3" y="6.5" width="12" height="11" rx="2" /><path d="M15 11l6-3.5v9L15 13z" /></>,
  mic: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" /></>,
  stop: <rect x="6" y="6" width="12" height="12" rx="2" />,
  save: <><path d="M3 7.5V18a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-8L10 5.5H4a1 1 0 0 0-1 1z" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  note: <><path d="M5 4h10l4 4v12H5z" /><path d="M8 12h8M8 16h5" /></>,
  bin: <path d="M4 7h16M9 7V4h6v3M6.5 7l1 13h9l1-13M10 11v6M14 11v6" />,
  share: <path d="M12 15V4M8 8l4-4 4 4M5 13v6h14v-6" />,
  link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
};
function Icon({ name, size = 28 }: { name: IconName; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">{ICON_PATHS[name]}</svg>
  );
}

/** One capture holds up to this many files (the server accepts 10 per capture). */
const MAX_BUNDLE = 10;

export function CaptureTiles({ onNotice, draftStore, moreHint }: {
  onNotice: (m: string) => void; draftStore?: DraftStore; moreHint?: React.ReactNode;
}) {
  const [taken, setTaken] = useState<File[]>([]);
  const [recording, setRecording] = useState(false);
  const [more, setMore] = useState<"closed" | "library" | "form">("closed");
  const photoRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const pickRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // A capture being decided on is kept on the phone as it changes, and brought
  // back if the page is thrown away and reloaded meanwhile. Nothing is saved
  // until the earlier one has been loaded, or an empty start would overwrite it.
  const [draftLoaded, setDraftLoaded] = useState(!draftStore);
  useEffect(() => {
    if (!draftStore) return;
    let live = true;
    draftStore.load()
      .then(files => { if (live && files.length) setTaken(prev => (prev.length ? prev : files)); })
      .catch(() => { /* an unreadable draft is no draft */ })
      .finally(() => { if (live) setDraftLoaded(true); });
    return () => { live = false; };
  }, [draftStore]);
  useEffect(() => {
    if (!draftStore || !draftLoaded) return;
    (taken.length ? draftStore.save(taken) : draftStore.clear()).catch(() => { /* best effort */ });
  }, [taken, draftLoaded, draftStore]);

  function chosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // With a bundle already open the new take joins it; otherwise it starts one.
    if (file) setTaken(prev => (prev.length >= MAX_BUNDLE ? prev : [...prev, file]));
    e.target.value = "";
  }

  // The microphone is a shared, device-level resource: if this page ever leaves it
  // open (a recording it never got to stop, a permission prompt answered after the
  // person had gone), the phone's audio/camera session can be left in a bad state
  // that only closing the browser app clears. So it is released on every way out.
  const streamRef = useRef<MediaStream | null>(null);
  const startingRef = useRef(false);
  const releaseMic = () => {
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch { /* already stopped */ }
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };
  useEffect(() => {
    const away = () => { releaseMic(); setRecording(false); };
    window.addEventListener("pagehide", away);
    return () => { window.removeEventListener("pagehide", away); releaseMic(); };
  }, []);

  async function toggleAudio() {
    if (recording) { recorderRef.current?.stop(); setRecording(false); return; }
    if (startingRef.current) return;               // a second tap while the permission prompt is up
    startingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const file = new File([blob], `audio-note-${Date.now()}.webm`, { type: blob.type });
        setTaken(prev => (prev.length >= MAX_BUNDLE ? prev : [...prev, file]));
        stream.getTracks().forEach(t => t.stop());
        if (streamRef.current === stream) streamRef.current = null;
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch {
      onNotice("Microphone access is needed to record an audio note.");
    } finally {
      startingRef.current = false;
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
      <input ref={photoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={chosen} data-testid="tile-photo-input" />
      <input ref={videoRef} type="file" accept="video/*" style={{ display: "none" }} onChange={chosen} data-testid="tile-video-input" />
      <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%", maxWidth: 320 }}>
        <button type="button" style={bigTile} onClick={() => photoRef.current?.click()} data-testid="tile-photo">
          <span style={bigIcon}><Icon name="camera" size={38} /></span>PHOTO
        </button>
        <button type="button" style={bigTile} onClick={() => videoRef.current?.click()} data-testid="tile-video">
          <span style={bigIcon}><Icon name="video" size={38} /></span>VIDEO
        </button>
        <button type="button" style={recording ? { ...bigTile, ...bigRecording } : bigTile} onClick={toggleAudio} data-testid="tile-audio">
          <span style={bigIcon}><Icon name={recording ? "stop" : "mic"} size={38} /></span>{recording ? "STOP" : "AUDIO"}
        </button>
      </div>

      <div style={{ width: "100%", maxWidth: 320 }}>
        <button type="button" style={{ ...secondary, width: "100%", minHeight: "2.4rem", fontSize: "0.85rem" }}
          aria-expanded={more !== "closed"} data-testid="capture-more"
          onClick={() => setMore(m => (m === "closed" ? "library" : "closed"))}>
          {more === "closed" ? "More ▾" : "More ▴"}
        </button>
      </div>
      {more !== "closed" && (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
          {moreHint}
          <button type="button" style={secondary} onClick={() => pickRef.current?.click()} data-testid="pick-from-phone">
            Camera not opening? Choose a photo or video from your phone
          </button>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button type="button" style={more === "library" ? primary : secondary} onClick={() => setMore("library")}>Library</button>
            <button type="button" style={more === "form" ? primary : secondary} onClick={() => setMore("form")}>Note or website</button>
          </div>
          {more === "library"
            ? <Library onNotice={onNotice} />
            : <CaptureForm onSent={() => setMore("library")} onNotice={onNotice} />}
        </div>
      )}

      <input ref={pickRef} type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={chosen} data-testid="tile-pick-input" />
      {taken.length > 0 && (
        <KeepSheet
          files={taken}
          recording={recording}
          onAdd={kind => (kind === "photo" ? photoRef.current?.click() : kind === "video" ? videoRef.current?.click() : toggleAudio())}
          onRemove={i => setTaken(prev => prev.filter((_, j) => j !== i))}
          onDone={msg => { setTaken([]); onNotice(msg); }}
          onDiscard={() => setTaken([])}
        />
      )}
    </div>
  );
}

/** The pop-up after a take — Drea's "what shall I do with this?" card
 *  (2026-10-27/28; the NuLife-wide action card, see docs/design/action-card.md).
 *  Squarer action tiles, not long buttons: Save (pick a folder) and Add more (a
 *  bundle), then Add note, then a small bin and share. Nothing leaves the screen
 *  until the server has said yes; a failure leaves the card open with the
 *  capture in it. */
function KeepSheet({ files, recording, onAdd, onRemove, onDone, onDiscard }: {
  files: File[]; recording: boolean;
  onAdd: (kind: "photo" | "video" | "audio") => void;
  onRemove: (index: number) => void;
  onDone: (message: string) => void; onDiscard: () => void;
}) {
  const api = useClient();
  const [panel, setPanel] = useState<"none" | "folders">("none");
  const [title, setTitle] = useState("");
  const [folders, setFolders] = useState<CaptureSection[] | null>(null);
  const [foldersError, setFoldersError] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState("");
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [binArmed, setBinArmed] = useState(false);
  const attemptId = useRef<string | null>(null);
  useEffect(() => { attemptId.current = null; }, [note, title, files]);

  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = files.map(f => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [files]);

  // Nothing left in the bundle means nothing to decide about.
  useEffect(() => { if (files.length === 0) onDiscard(); }, [files.length]);

  async function openFolders() {
    if (panel === "folders") { setPanel("none"); return; }
    setPanel("folders");
    try { setFolders(await api.getCaptureSections()); setFoldersError(null); }
    catch (err) { setFoldersError(err instanceof Error ? err.message : "Could not load the folders."); }
  }

  async function save(tag?: string) {
    if (sending) return;
    setSending(true); setError(null);
    try {
      const saved = await api.uploadCapture({
        title: title.trim() || undefined, note: note.trim() || undefined, tag: tag?.trim() || undefined, files,
        clientActionId: (attemptId.current ??= crypto.randomUUID()),
      });
      attemptId.current = null;
      const folder = tag?.trim();
      // A queued save is on the phone and not yet on the server: say so.
      onDone(saved.queued
        ? waitingNote(saved, folder ? ` for ${folder}` : "")
        : folder ? `Saved to ${folder}.` : "Saved to your Inbox.");
    } catch (err) {
      const why = err instanceof Error ? err.message : "Could not save the capture.";
      setError(`${why} Nothing has been lost — it is still here. Tap Save to try again.`);
    } finally {
      setSending(false);
    }
  }

  const full = files.length >= MAX_BUNDLE;
  const lastType = files[files.length - 1]?.type ?? "";
  const lastKind: "photo" | "video" | "audio" = lastType.startsWith("video/") ? "video" : lastType.startsWith("audio/") ? "audio" : "photo";
  const otherKinds = (["photo", "video", "audio"] as const).filter(k => k !== lastKind);
  return (
    <div role="dialog" aria-modal="true" aria-label="Keep this capture" data-testid="keep-sheet"
      style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{
        width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", boxSizing: "border-box",
        background: "var(--nl-bg-raised, var(--nl-surface, #fff))", color: "var(--nl-ink)",
        borderRadius: "20px 20px 0 0", padding: "18px 18px calc(18px + env(safe-area-inset-bottom))",
        display: "flex", flexDirection: "column", gap: 12,
      }}>
        <strong style={{ fontSize: "1.05rem" }}>Mira · what shall I do with this?</strong>

        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }} data-testid="keep-bundle">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} style={thumbBox} data-testid="keep-thumb">
              {previews[i] && f.type.startsWith("image/") ? <img src={previews[i]} alt="" style={thumbFill} />
                : previews[i] && f.type.startsWith("video/") ? <video src={previews[i]} muted playsInline preload="metadata" style={thumbFill} />
                : <Icon name={f.type.startsWith("audio/") ? "mic" : "note"} size={30} />}
              <button type="button" aria-label={`Remove ${f.name}`} disabled={sending} onClick={() => onRemove(i)} style={thumbX}>×</button>
            </div>
          ))}
        </div>
        {files.length === 1 && files[0]!.type.startsWith("audio/") && previews[0] && <audio src={previews[0]} controls style={{ width: "100%" }} />}

        {error && <div role="alert" style={{ color: "var(--nl-danger, var(--nl-status-attention))", fontSize: "0.9rem" }}>{error}</div>}

        <input value={title} onChange={e => setTitle(e.target.value)} maxLength={255} data-testid="keep-name"
          aria-label={files.length > 1 ? "Name this bundle" : "Name this capture"}
          placeholder={files.length > 1 ? "Name this bundle (optional)" : "Name this (optional)"} style={field} />

        {/* Two bigger tiles on top: another of what was just taken, and a note.
            Two smaller squares and Save underneath. One tap to take another. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {([lastKind, "note"] as const).map(k => (
            <button type="button" key={k} style={actionTile} disabled={sending || (k !== "note" && full)}
              data-testid={k === "note" ? "keep-add-note" : `keep-add-${k}`}
              onClick={() => (k === "note" ? setShowNote(v => !v) : onAdd(k))}>
              <span style={actionIcon}><Icon name={k === "note" ? "note" : k === "photo" ? "camera" : k === "video" ? "video" : recording ? "stop" : "mic"} size={30} /></span>
              {k === "audio" && recording ? "Stop" : `+ ${KIND_LABEL[k]}`}
            </button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {otherKinds.map(k => (
            <button type="button" key={k} style={smallTile} disabled={sending || full} data-testid={`keep-add-${k}`} onClick={() => onAdd(k)}>
              <Icon name={k === "photo" ? "camera" : k === "video" ? "video" : recording ? "stop" : "mic"} size={22} />
              {k === "audio" && recording ? "Stop" : `+ ${KIND_LABEL[k]}`}
            </button>
          ))}
          <button type="button" style={saveTile} disabled={sending} onClick={openFolders} data-testid="keep-save" aria-expanded={panel === "folders"}>
            <Icon name="save" size={22} />{sending ? "Saving…" : "Save"}
          </button>
        </div>
        {full && <span style={{ fontSize: "0.85rem", opacity: 0.7 }}>A bundle holds up to {MAX_BUNDLE}.</span>}

        {showNote && (
          <textarea value={note} autoFocus onChange={e => setNote(e.target.value)} rows={3} aria-label="A note"
            placeholder="A note — tap the mic on your keyboard to dictate" style={{ ...field, padding: "8px 12px", resize: "vertical" }} />
        )}

        {panel === "folders" && (
          <div style={folderPanel} data-testid="keep-folders">
            {foldersError ? <div role="alert" style={{ padding: 10 }}>{foldersError}</div>
              : folders === null ? <div style={{ padding: 10 }}><Loading /></div>
              : (
                <div style={{ maxHeight: "38vh", minHeight: 160, overflowY: "auto" }} data-testid="keep-folder-list">
                  <button type="button" style={dropRow} disabled={sending} onClick={() => save()} data-testid="keep-inbox"><strong>Inbox</strong><span style={{ opacity: 0.6 }}>no folder</span></button>
                  {folders.map(s => (
                    <button type="button" key={s.tagKey} style={dropRow} disabled={sending} onClick={() => save(s.tag)}>
                      <span>{s.tag}</span><span style={{ opacity: 0.6 }}>{s.count}</span>
                    </button>
                  ))}
                </div>
              )}
            {creating ? (
              <div style={{ display: "flex", gap: 8, padding: 8, borderTop: "1px solid var(--nl-stroke)" }}>
                <input value={newFolder} autoFocus onChange={e => setNewFolder(e.target.value)} placeholder="Folder name, e.g. Garden" style={field} aria-label="New folder name" />
                <button type="button" style={primary} disabled={sending || !newFolder.trim()} onClick={() => save(newFolder)} data-testid="keep-create">Save</button>
              </div>
            ) : (
              <button type="button" style={{ ...dropRow, borderTop: "1px solid var(--nl-stroke)", fontWeight: 600 }} onClick={() => setCreating(true)} data-testid="keep-new-folder">＋ New folder</button>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button type="button" disabled={sending} data-testid="keep-discard"
            aria-label={binArmed ? "Tap again to throw this away" : "Throw this away"}
            onClick={() => (binArmed ? onDiscard() : setBinArmed(true))}
            onBlur={() => setBinArmed(false)}
            style={binArmed ? { ...iconButton, ...bigRecording, width: "auto", padding: "0 14px" } : iconButton}>
            <Icon name="bin" size={22} />{binArmed && <span style={{ fontSize: "0.8rem", marginLeft: 6 }}>Tap again to throw away</span>}
          </button>
          {/* Share is deliberately unplugged: it will send a capture or bundle to
              someone else on the app, and nothing sends yet. */}
          <button type="button" disabled aria-label="Share (coming soon)" title="Share — coming soon" style={{ ...iconButton, opacity: 0.35 }} data-testid="keep-share"><Icon name="share" size={22} /></button>
        </div>
      </div>
    </div>
  );
}

// ── Library ───────────────────────────────────────────────────────────────

function Library({ onNotice }: { onNotice: (m: string) => void }) {
  const api = useClient();
  const [target, setTarget] = useState<LibraryTarget>({ kind: "sections" });
  const [sections, setSections] = useState<CaptureSection[] | null>(null);
  const [items, setItems] = useState<CaptureItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  // Loading, failed, and loaded stay three distinct states — a failed fetch
  // must not render as "no sections yet" / "nothing here", which would be
  // this screen making the same claim useAsync.ts's own header warns
  // against: an empty view says nothing is there, and saying that because a
  // request failed is not honest.
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [itemsError, setItemsError] = useState<string | null>(null);
  /** From the server, never written here — a screen promising 30 days over a
   *  job that purges after 14 is worse than promising nothing. */
  const [retentionDays, setRetentionDays] = useState<number | null>(null);

  async function loadSections() {
    try {
      setSections(await api.getCaptureSections());
      setSectionsError(null);
    } catch (err) {
      setSectionsError(err instanceof Error ? err.message : "Could not load sections.");
    }
  }
  async function loadItems(t: LibraryTarget) {
    if (t.kind === "sections") return;
    setLoading(true);
    try {
      // Both paths answer the same shape, so the retention window is known
      // on the ordinary list too — which is what lets the message after a
      // delete say how long there is to change your mind.
      const { captures, retentionDays } = t.kind === "deleted"
        ? await api.getDeletedCaptures()
        : await api.getCaptures(t.kind === "inbox" ? null : t.tag);
      setItems(captures);
      setRetentionDays(retentionDays);
      setItemsError(null);
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : "Could not load this section.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadSections(); }, []);
  useEffect(() => { loadItems(target); }, [target]);

  async function refresh() {
    await Promise.all([loadSections(), loadItems(target)]);
  }

  if (target.kind === "sections") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button type="button" style={{ ...card, textAlign: "left", cursor: "pointer" }} onClick={() => setTarget({ kind: "inbox" })}>
          <strong>Inbox</strong>
          <span style={{ color: "var(--nl-ink-dim)", marginLeft: 8, fontSize: "0.85rem" }}>everything without a section</span>
        </button>
        {/* Deliberately below the Inbox and above the sections: it is where
            somebody looks having just realised they deleted the wrong thing,
            and it should not be the first thing they see every other time. */}
        <button type="button" style={{ ...card, textAlign: "left", cursor: "pointer" }}
          onClick={() => setTarget({ kind: "deleted" })} data-testid="open-deleted">
          <strong>Recently deleted</strong>
          <span style={{ color: "var(--nl-ink-dim)", marginLeft: 8, fontSize: "0.85rem" }}>
            still restorable
          </span>
        </button>
        {sectionsError ? <Message tone="bad">{sectionsError}</Message>
        : sections === null ? <Loading /> : sections.length === 0 ? (
          <p style={hint}>No sections yet — tag a capture to start one.</p>
        ) : sections.map(s => (
          <button type="button" key={s.tagKey} style={{ ...card, textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={() => setTarget({ kind: "section", tag: s.tag, tagKey: s.tagKey })}>
            <strong style={{ flex: 1 }}>{s.tag}</strong>
            <span style={{ color: "var(--nl-ink-dim)", fontSize: "0.85rem" }}>{s.count}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button type="button" style={secondary} onClick={() => setTarget({ kind: "sections" })}>← Sections</button>
        <strong>
          {target.kind === "inbox" ? "Inbox"
            : target.kind === "deleted" ? "Recently deleted"
            : target.tag}
        </strong>
        {target.kind === "section" && (
          <RenameSectionButton tagKey={target.tagKey} currentName={target.tag} onRenamed={newTag => {
            setTarget({ kind: "section", tag: newTag, tagKey: newTag.trim().toLowerCase() });
            onNotice("Section renamed.");
            refresh();
          }} />
        )}
      </div>
      {target.kind === "deleted" && retentionDays !== null && (
        <p style={hint}>
          Deleted captures stay here for {retentionDays} days, then the files are
          removed for good.
        </p>
      )}
      {itemsError ? <Message tone="bad">{itemsError}</Message>
      : loading || items === null ? <Loading /> : items.length === 0 ? (
        <p style={hint}>
          {target.kind === "deleted" ? "Nothing deleted." : "Nothing here."}
        </p>
      ) : items.map(c => (
        <CaptureRow
          key={c.id}
          capture={c}
          deleted={target.kind === "deleted"}
          retentionDays={retentionDays}
          onChanged={refresh}
          onNotice={onNotice}
        />
      ))}
    </div>
  );
}

function RenameSectionButton({ tagKey, currentName, onRenamed }: {
  tagKey: string; currentName: string; onRenamed: (newTag: string) => void;
}) {
  const api = useClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentName);
  if (!editing) {
    return <button type="button" style={{ ...secondary, marginLeft: "auto", fontSize: "0.8rem" }} onClick={() => setEditing(true)}>Rename section</button>;
  }
  return (
    <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
      <input value={value} onChange={e => setValue(e.target.value)} style={{ ...field, width: 140 }} />
      <button type="button" style={primary} onClick={async () => {
        const newTag = value.trim();
        if (!newTag) return;
        await api.renameCaptureSection(tagKey, newTag);
        setEditing(false);
        onRenamed(newTag);
      }}>Save</button>
      <button type="button" style={secondary} onClick={() => setEditing(false)}>Cancel</button>
    </div>
  );
}

function CaptureRow({ capture, deleted = false, retentionDays, onChanged, onNotice }: {
  capture: CaptureItem;
  /** Shown on the recently-deleted list: Restore instead of the usual
   *  actions, and how long is left. */
  deleted?: boolean;
  retentionDays?: number | null;
  onChanged: () => void;
  onNotice: (m: string) => void;
}) {
  const api = useClient();
  const [editing, setEditing] = useState<"title" | "move" | null>(null);
  const [draft, setDraft] = useState("");

  const image = capture.files.find(f => f.mimeType.startsWith("image/"));
  const video = capture.files.find(f => f.mimeType.startsWith("video/"));
  const audio = capture.files.find(f => f.mimeType.startsWith("audio/"));
  const other = capture.files.filter(f => f !== image);
  // A saved website's preview arrives a moment after the capture: look again a
  // few times, then stop (a page that never answers must not poll for ever).
  const looks = useRef(0);
  useEffect(() => {
    if (deleted || !capture.linkPreviewPending || looks.current >= 6) return;
    looks.current += 1;
    const t = setTimeout(onChanged, 2500);
    return () => clearTimeout(t);
  }, [capture.linkPreviewPending, capture.updatedAt, deleted]);

  const preview = capture.linkPreview;
  const host = capture.link ? new URL(capture.link).hostname.replace(/^www\./, "") : null;

  async function saveEdit() {
    if (editing === "title") await api.saveCapture(capture.id, { title: draft.trim() || null });
    else if (editing === "move") await api.saveCapture(capture.id, { tag: draft.trim() || null });
    setEditing(null);
    onChanged();
  }

  async function sendCapture() {
    const shareFiles = await Promise.all(
      capture.files.map(async f => {
        const blob = await fetch(f.url).then(r => r.blob());
        return new File([blob], f.filename, { type: f.mimeType });
      }),
    );
    // `typeof navigator.share === "function"`, not `navigator.share &&` —
    // the DOM types declare it as always present, so a plain truthiness
    // check is (wrongly, for this API) flagged as dead code by tsc even
    // though plenty of real browsers genuinely have no `share` at all.
    const hasShare = typeof navigator.share === "function";
    const canShareFiles = hasShare && (navigator as any).canShare?.({ files: shareFiles });
    if (canShareFiles) {
      try { await navigator.share({ files: shareFiles, text: capture.note ?? undefined }); }
      catch { /* cancelled — not an error */ }
    } else if (hasShare) {
      try { await navigator.share({ text: capture.note ?? capture.title ?? "" }); }
      catch { /* cancelled */ }
    } else {
      onNotice("Sharing isn't available in this browser.");
    }
  }

  async function remove() {
    await api.deleteCapture(capture.id);
    // Says where it went, not just that it went. "Removed." left somebody
    // with no idea there was anything to go back to.
    onNotice(
      retentionDays
        ? `Deleted — in Recently deleted for ${retentionDays} days if you need it back.`
        : "Deleted — in Recently deleted if you need it back.",
    );
    onChanged();
  }

  async function restore() {
    try {
      await api.restoreCapture(capture.id);
      onNotice("Back in the library.");
      onChanged();
    } catch (err) {
      onNotice(err instanceof Error ? err.message : "Could not put that back.");
    }
  }

  /**
   * How long is left, in whole days, or null when it cannot be worked out.
   *
   * Rounded UP: a capture with eight hours left reads "1 day left", not "0".
   * Rounding down would tell somebody a thing is already gone while they are
   * looking at it, and the whole point of this list is being able to trust it.
   */
  const daysLeft = (() => {
    if (!capture.archivedAt || !retentionDays) return null;
    const goesAt = capture.archivedAt.getTime() + retentionDays * 86_400_000;
    return Math.max(0, Math.ceil((goesAt - Date.now()) / 86_400_000));
  })();

  return (
    <div style={card}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        {image || preview ? (
          <img src={(image ?? preview)!.thumbnailUrl ?? (image ?? preview)!.url} alt="" data-testid={image ? undefined : "capture-link-preview"}
            style={{ width: 56, height: 56, borderRadius: "var(--nl-radius-chip, 6px)", objectFit: "cover", flexShrink: 0 }} />
        ) : other[0] || capture.link ? (
          <div style={{ width: 56, height: 56, borderRadius: "var(--nl-radius-chip, 6px)", background: "var(--nl-surface-raised, var(--nl-tile-hover))", border: "1px solid var(--nl-stroke)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0 }}>
            {video ? <Icon name="video" size={28} /> : other[0] ? <Icon name="mic" size={28} /> : <Icon name="link" size={28} />}
          </div>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          {editing === "title" ? (
            <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Title" style={{ ...field, marginBottom: 4 }} autoFocus />
          ) : (
            <strong>{capture.title ?? capture.note?.slice(0, 60) ?? capture.linkTitle ?? host ?? capture.createdAt.toLocaleString("en-GB")}</strong>
          )}
          {capture.note && editing !== "title" && (
            <p style={{ margin: "2px 0 0", fontSize: "0.85rem", color: "var(--nl-ink-dim)" }}>{capture.note}</p>
          )}
          {capture.linkDescription && editing !== "title" && !capture.note && (
            <p data-testid="capture-link-description"
              style={{ margin: "2px 0 0", fontSize: "0.85rem", color: "var(--nl-ink-dim)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {capture.linkDescription}
            </p>
          )}
          {capture.link && editing !== "title" && (
            <a href={capture.link} target="_blank" rel="noopener noreferrer" data-testid="capture-open-link"
              style={{ display: "block", margin: "2px 0 0", fontSize: "0.85rem", wordBreak: "break-all" }}>
              {host} ↗
            </a>
          )}
          {!deleted && video && (
            <video src={video.url} controls preload="metadata" playsInline
              style={{ display: "block", width: "100%", maxWidth: 360, marginTop: 6, borderRadius: "var(--nl-radius-chip, 6px)" }} />
          )}
          {!deleted && audio && (
            <audio src={audio.url} controls preload="none" style={{ display: "block", width: "100%", maxWidth: 360, marginTop: 6 }} />
          )}
          {editing === "move" && (
            <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Move to section (blank = Inbox)" style={{ ...field, marginTop: 6 }} autoFocus />
          )}
          <div style={{ fontSize: "0.75rem", color: "var(--nl-ink-faint)", marginTop: 4 }}>
            {capture.createdAt.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            {other.length > 0 && image ? ` · ${other.length} more file(s)` : ""}
            {deleted && daysLeft !== null && (
              <span data-testid="days-left">
                {` · ${daysLeft === 1 ? "1 day left" : `${daysLeft} days left`}`}
              </span>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        {editing ? (
          <>
            <button type="button" style={primary} onClick={saveEdit}>Save</button>
            <button type="button" style={secondary} onClick={() => setEditing(null)}>Cancel</button>
          </>
        ) : deleted ? (
          // Restore, and nothing else. Renaming or re-filing something that is
          // on its way to being purged is an edit nobody meant to make.
          <button type="button" style={primary} onClick={restore} data-testid="restore-capture">
            Restore
          </button>
        ) : (
          <>
            <button type="button" style={rowAction} onClick={() => { setEditing("move"); setDraft(capture.tag ?? ""); }}>Move</button>
            <button type="button" style={rowAction} onClick={() => { setEditing("title"); setDraft(capture.title ?? ""); }}>Rename</button>
            {capture.link && (
              <button type="button" style={rowAction} data-testid="refresh-link"
                onClick={async () => { await api.refreshCaptureLink(capture.id); looks.current = 0; onNotice("Looking at the page again…"); setTimeout(onChanged, 2500); }}>
                Refresh preview
              </button>
            )}
            <button type="button" style={rowAction} onClick={sendCapture}>Send</button>
            <button type="button" style={rowAction} onClick={remove} data-testid="delete-capture">Delete</button>
          </>
        )}
      </div>
    </div>
  );
}

const card: React.CSSProperties = {
  background: "var(--nl-surface, var(--nl-tile))",
  border: "1px solid var(--nl-stroke)",
  borderRadius: "var(--nl-radius-card, var(--nl-radius-sm))",
  padding: 12,
};

const hint: React.CSSProperties = {
  display: "block", margin: "4px 0", fontSize: "0.8rem", color: "var(--nl-ink-dim)",
};

const field: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)",
  borderRadius: "var(--nl-radius-control, var(--nl-radius-sm))",
  border: "1px solid var(--nl-stroke)",
  background: "var(--nl-surface-raised, var(--nl-tile-hover))",
  color: "var(--nl-ink)",
  padding: "0 12px",
  font: "inherit",
  width: "100%",
};

const primary: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)", padding: "0 18px", fontWeight: 600,
  borderRadius: "var(--nl-radius-control, var(--nl-radius-sm))", border: "1px solid var(--nl-stroke)",
  background: "var(--nl-state-open, var(--nl-send))", color: "var(--nl-state-open-ink, var(--nl-send-ink))",
  fontFamily: "inherit", fontSize: "inherit", cursor: "pointer",
};

const secondary: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)", padding: "0 16px",
  borderRadius: "var(--nl-radius-control, var(--nl-radius-sm))", border: "1px solid var(--nl-stroke)",
  background: "var(--nl-surface-raised, var(--nl-tile-hover))", color: "var(--nl-ink)",
  font: "inherit", cursor: "pointer",
};

const rowAction: React.CSSProperties = {
  ...secondary, minHeight: "1.9rem", padding: "0 10px", fontSize: "0.8rem",
};

const danger: React.CSSProperties = {
  ...secondary, background: "var(--nl-danger-soft, var(--nl-notice-problem-bg))", color: "var(--nl-danger, var(--nl-status-attention))",
};

// Sized and spaced like the home screen's main tiles: wide, tall, centred.
const bigTile: React.CSSProperties = {
  minHeight: 112, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
  borderRadius: "var(--nl-radius, var(--nl-radius-card))",
  border: "1px solid var(--nl-band-stroke, var(--nl-stroke))",
  background: "var(--nl-band, var(--nl-surface-raised, var(--nl-tile-hover)))",
  color: "var(--nl-band-ink, var(--nl-ink))",
  font: "inherit", fontWeight: 700, letterSpacing: "0.12em", fontSize: "1rem", cursor: "pointer",
};
const bigIcon: React.CSSProperties = { display: "flex", lineHeight: 1 };
const bigRecording: React.CSSProperties = {
  background: "var(--nl-danger-soft, var(--nl-notice-problem-bg))", color: "var(--nl-danger, var(--nl-status-attention))",
};
const actionTile: React.CSSProperties = {
  minHeight: 96, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
  borderRadius: "var(--nl-radius, var(--nl-radius-card))",
  border: "1px solid var(--nl-band-stroke, var(--nl-stroke))",
  background: "var(--nl-band, var(--nl-surface-raised, var(--nl-tile-hover)))",
  color: "var(--nl-band-ink, var(--nl-ink))", font: "inherit", fontWeight: 700, cursor: "pointer",
};
const actionIcon: React.CSSProperties = { display: "flex", lineHeight: 1 };
/** What to say when a save is on the phone but the server has not confirmed it.
 *  Says the real reason, so a slow upload or a sign-in is never blamed on signal. */
function waitingNote(saved: { queuedWhy?: string }, forFolder: string): string {
  const why = saved.queuedWhy ?? "offline";
  if (why === "offline") return `Saved on your phone${forFolder} — it will send when you have signal.`;
  if (why === "signin") return `Saved on your phone${forFolder} — sign in again and it will send.`;
  return `Saved on your phone${forFolder} and still sending — it will go as soon as it can (${why}).`;
}
const KIND_LABEL = { photo: "Photo", video: "Video", audio: "Audio", note: "Note" } as const;
const smallTile: React.CSSProperties = { ...actionTile, minHeight: 72, gap: 4, fontSize: "0.9rem" };
// Save is plum (Drea, 2026-10-31): the one tile that finishes things. The same
// plum as the home screen's picked/ADD tiles.
const saveTile: React.CSSProperties = {
  ...smallTile, background: "var(--nl-state-selected, linear-gradient(150deg, #7a4d56 0%, #4f3238 100%))",
  color: "var(--nl-state-selected-ink, #ffffff)", border: "1px solid transparent",
};
// The folder list is the lightest part of the card and tall enough to read.
const folderPanel: React.CSSProperties = {
  border: "1px solid var(--nl-stroke)", borderRadius: "var(--nl-radius-sm)", overflow: "hidden",
  background: "var(--nl-cream, #fbf9f3)", color: "var(--nl-ink, #2b2b24)",
};
const dropdown: React.CSSProperties = {
  border: "1px solid var(--nl-stroke)", borderRadius: "var(--nl-radius-sm)", overflow: "hidden",
  background: "var(--nl-surface-raised, var(--nl-tile-hover))",
};
const dropRow: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", minHeight: "var(--nl-hit-min)",
  padding: "0 14px", border: "none", borderBottom: "1px solid var(--nl-stroke)", background: "transparent",
  color: "var(--nl-ink)", font: "inherit", textAlign: "left", cursor: "pointer",
};
const miniChoice: React.CSSProperties = { ...secondary, flex: 1, padding: "0 8px", fontSize: "0.9rem", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 };
const thumbBox: React.CSSProperties = {
  position: "relative", flex: "0 0 auto", width: 72, height: 72, borderRadius: 12, overflow: "hidden",
  background: "var(--nl-surface-raised, var(--nl-tile-hover))", border: "1px solid var(--nl-stroke)",
  display: "flex", alignItems: "center", justifyContent: "center",
};
const thumbFill: React.CSSProperties = { width: "100%", height: "100%", objectFit: "cover" };
const thumbX: React.CSSProperties = {
  position: "absolute", top: 2, right: 2, width: 24, height: 24, borderRadius: 12, border: "none",
  background: "rgba(0,0,0,0.65)", color: "#fff", fontSize: "1rem", lineHeight: 1, cursor: "pointer", padding: 0,
};
const iconButton: React.CSSProperties = {
  ...secondary, width: 44, minHeight: 44, padding: 0, fontSize: "1.2rem", display: "flex", alignItems: "center", justifyContent: "center",
};
const sheetSub: React.CSSProperties = { fontSize: "0.75rem", fontWeight: 400, opacity: 0.7 };
